import { lazy, useEffect } from 'react'
import { DndProvider } from 'react-dnd'
import { useSelector } from 'react-redux'
import { RootState } from './store/store'
import ProtectedRoute from './utils/ProtectedRoute'
import { HTML5Backend } from 'react-dnd-html5-backend'
import PageLoader from './components/shared/PageLoader'
import { GoogleOAuthProvider } from '@react-oauth/google'
import GameNotFound from './components/shared/GameNotFound'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import DisplayTutorial from './components/shared/DisplayTutorial'
import LeaderBoard from './pages/dashboard/leaderboard/Leaderboard'
import TawkIntegration from './components/shared/TawkIntegration'

const GameEditor = lazy(() => import('./pages/edit/GameEditor'))
const Preview = lazy(() => import('./pages/edit/components/Preview'))
const MagicLinkValidate = lazy(() => import('./pages/auth/MagicLinkValidate'))

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_AUTH_URL

// 🔥 Handles redirect logic for "My Arcade"
const RedirectToArcade = () => {
  useEffect(() => {
    try {
      const stored = localStorage.getItem('userInfo')
      let url = 'https://play.aicade.io' // default fallback
      if (stored) {
        const user = JSON.parse(stored)
        if (user?.userId) {
          url = `https://play.aicade.io/profile/${user.userId}`
        }
      }
      window.location.href = url
    } catch {
      window.location.href = 'https://play.aicade.io'
    }
  }, [])

  return null // no UI, just redirect
}

const App = () => {
  const displayTutorial = useSelector(
    (state: RootState) => state.tutorial.displayTutorial
  )
  const previewMode = useSelector(
    (state: RootState) => state.system.previewMode
  )
  const isLoggedIn = useSelector(
    (state: RootState) => state.user.isLoggedIn
  )

  return (
    <DndProvider backend={HTML5Backend}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <BrowserRouter
          basename="/"
          future={{ v7_relativeSplatPath: true, v7_startTransition: true }}
        >
          <Routes>
            <Route element={<ProtectedRoute />}>
              <Route
                path="/"
                element={
                  <PageLoader>
                    <GameEditor />
                  </PageLoader>
                }
              >
                <Route
                  path="/:id"
                  element={
                    <PageLoader>
                      <GameEditor />
                    </PageLoader>
                  }
                />
              </Route>

              {/* 🔥 Replaced MyGames with redirect logic */}
              <Route
                path="/my-arcade"
                element={<RedirectToArcade />}
              />

              <Route
                path="/dashboard/leaderboard"
                element={
                  <PageLoader>
                    <LeaderBoard />
                  </PageLoader>
                }
              />
            </Route>

            <Route
              path="/validate"
              element={
                <PageLoader>
                  <MagicLinkValidate />
                </PageLoader>
              }
            />

            <Route
              path="*"
              element={
                <PageLoader>
                  <GameNotFound />
                </PageLoader>
              }
            />
          </Routes>
        </BrowserRouter>

        {previewMode && <Preview />}
        {displayTutorial && <DisplayTutorial />}
        {isLoggedIn && <TawkIntegration />}
      </GoogleOAuthProvider>
    </DndProvider>
  )
}

export default App
